import { useCallback, useEffect, useRef, useState } from 'react';

// Dragging a comparison's column by its area's name to another place, in the card (CompareCard.js) and on the
// comparison's page (ComparePage.js; the owner, 2026-11-01). A mouse picks the column up once it has moved a few
// pixels, so a click on the name still works; a finger after a short press, so a swipe still scrolls the columns
// sideways. The columns trade places as the pointer crosses them (each column's head cell carries
// `data-compare-col` with its code) and the scroller follows near its edges. Letting go keeps the order
// (onReorder with the codes in their new order), Escape puts it back, and the click that ends a drag is swallowed.
// A phone decides when a finger goes down whether its moves may scroll; a touchmove listener added after that is
// ignored, and the columns scrolled under the held finger and cancelled the drag. So each handle carries its own
// non-passive touchmove listener from the start (`ref` in handleProps), which stops the scroll once a column is held.

const MOUSE_SLOP = 5;
const TOUCH_SLOP = 8;
const PRESS_MS = 280;
// Near the scroller's edges (inside the measures' names on the left) it scrolls, faster closer in.
const EDGE = 48;
const MAX_SPEED = 14;

const sameOrder = (a, b) => a.length === b.length && a.every((code, index) => code === b[index]);

export default function useColumnDrag({ codes, onReorder, scrollRef, enabled = true }) {
    const [drag, setDrag] = useState(null); // { code, order } while a column is held
    const gestureRef = useRef(null);
    const codesRef = useRef(codes);
    codesRef.current = codes;
    const onReorderRef = useRef(onReorder);
    onReorderRef.current = onReorder;
    const handlesRef = useRef(new WeakSet());

    // The column under the pointer, from the columns as drawn (the order a re-render hasn't shown yet is skipped,
    // or a move before it would swap the column back).
    const retarget = useCallback(() => {
        const gesture = gestureRef.current;
        const scroller = scrollRef.current;
        if (!gesture?.active || !scroller) return;
        const cells = [...scroller.querySelectorAll('[data-compare-col]')];
        if (!cells.length) return;
        const drawn = cells.map((cell) => cell.getAttribute('data-compare-col'));
        let target = cells.findIndex((cell) => gesture.x < cell.getBoundingClientRect().right);
        if (target < 0) target = cells.length - 1;
        const order = drawn.filter((code) => code !== gesture.code);
        order.splice(target, 0, gesture.code);
        if (sameOrder(order, gesture.order)) return;
        gesture.order = order;
        setDrag({ code: gesture.code, order });
    }, [scrollRef]);

    const follow = useCallback(() => {
        const gesture = gestureRef.current;
        const scroller = scrollRef.current;
        if (!gesture?.active || !scroller) return;
        const rect = scroller.getBoundingClientRect();
        const names = scroller.querySelector('thead th');
        const left = rect.left + (names ? names.getBoundingClientRect().width : 0);
        let speed = 0;
        if (gesture.x > rect.right - EDGE) speed = Math.min(1, (gesture.x - (rect.right - EDGE)) / EDGE);
        else if (gesture.x < left + EDGE) speed = -Math.min(1, (left + EDGE - gesture.x) / EDGE);
        if (speed) {
            const before = scroller.scrollLeft;
            scroller.scrollLeft += speed * MAX_SPEED;
            if (scroller.scrollLeft !== before) retarget();
        }
        gesture.frame = requestAnimationFrame(follow);
    }, [scrollRef, retarget]);

    const finish = useCallback((commit) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        gestureRef.current = null;
        clearTimeout(gesture.timer);
        cancelAnimationFrame(gesture.frame);
        gesture.detach();
        if (!gesture.active) return;
        document.body.classList.remove('is-column-dragging');
        setDrag(null);
        if (commit) {
            // The click the button's release makes would open the name's link.
            const swallow = (event) => { event.preventDefault(); event.stopPropagation(); };
            window.addEventListener('click', swallow, true);
            setTimeout(() => window.removeEventListener('click', swallow, true), 300);
            if (!sameOrder(gesture.order, codesRef.current)) onReorderRef.current?.(gesture.order);
        }
    }, []);

    const begin = useCallback(() => {
        const gesture = gestureRef.current;
        if (!gesture || gesture.active) return;
        gesture.active = true;
        gesture.order = codesRef.current.slice();
        document.body.classList.add('is-column-dragging');
        if (gesture.touch) navigator.vibrate?.(10);
        setDrag({ code: gesture.code, order: gesture.order });
        gesture.frame = requestAnimationFrame(follow);
    }, [follow]);

    const onPointerDown = useCallback((event, code) => {
        if (!enabled || !event.isPrimary || event.button !== 0 || gestureRef.current) return;
        const touch = event.pointerType === 'touch';
        const gesture = {
            code, touch, active: false, startX: event.clientX, startY: event.clientY, x: event.clientX, order: codesRef.current,
        };
        const onMove = (moveEvent) => {
            if (moveEvent.pointerId !== event.pointerId) return;
            gesture.x = moveEvent.clientX;
            if (gesture.active) {
                moveEvent.preventDefault();
                retarget();
                return;
            }
            const moved = Math.hypot(moveEvent.clientX - gesture.startX, moveEvent.clientY - gesture.startY);
            if (touch && moved > TOUCH_SLOP) finish(false); // a swipe: the columns scroll instead
            else if (!touch && moved > MOUSE_SLOP) begin();
        };
        const onUp = (upEvent) => { if (upEvent.pointerId === event.pointerId) finish(true); };
        const onCancel = (cancelEvent) => { if (cancelEvent.pointerId === event.pointerId) finish(false); };
        const onKey = (keyEvent) => { if (keyEvent.key === 'Escape' && gesture.active) finish(false); };
        // A held finger: no long-press menu (the scroll under it is stopped by the handle's own listener, below).
        const onMenu = (menuEvent) => menuEvent.preventDefault();
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('keydown', onKey);
        window.addEventListener('contextmenu', onMenu, true);
        gesture.detach = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('contextmenu', onMenu, true);
        };
        gestureRef.current = gesture;
        if (touch) gesture.timer = setTimeout(begin, PRESS_MS);
    }, [enabled, retarget, finish, begin]);

    useEffect(() => () => finish(false), [finish]);

    // A finger's moves go to the element it went down on, so the handle's listener sees them all. Until a column is
    // held they pass, so a swipe that starts on a name still scrolls the columns.
    const attachHandle = useCallback((element) => {
        if (!element || handlesRef.current.has(element)) return;
        handlesRef.current.add(element);
        element.addEventListener('touchmove', (event) => {
            if (gestureRef.current?.active && event.cancelable) event.preventDefault();
        }, { passive: false });
    }, []);

    const handleProps = useCallback((code) => (enabled
        ? {
            ref: attachHandle,
            onPointerDown: (event) => onPointerDown(event, code),
            onDragStart: (event) => event.preventDefault(),
            draggable: false,
        }
        : {}), [enabled, onPointerDown, attachHandle]);

    return { order: drag ? drag.order : codes, dragging: drag?.code || null, handleProps };
}
